import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Plus,
  Minus,
  Printer,
  Check,
  X,
  Eye,
  EyeClosed,
  Search,
  ChevronDown,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppSelector } from "../../store/hooks";
import { hasFeatureAccess, FEATURES } from "../../config/roleFeatures";
import PublishSaleModal from "./components/PublishSaleModal";
import CancelSaleModal from "./components/CancelSaleModal";
import AddProductModal from "./components/AddProduct";
import StatusBadge from "./components/StatusBadge";
import DeleteIcon from "../../assets/delete-modal-icon.svg";
import { useViewSalesData } from "./hooks/useViewSalesData";
import { customersAPI, salesAPI } from "../../services";
import { useDebounce } from "../../hooks/useDebounce";

import SaveIcon from "../../assets/icons/SaveIcon";
import NameEditIcon from "../../assets/icons/NameEditIcon";
import RedDeleteIcon from "../../assets/icons/RedDeleteIcon";

// Removed dummy image imports and helper function

const ViewSales = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAppSelector((state) => state.auth);
  const userRole = user?.admin_role?.toUpperCase();
  const canViewProfits = hasFeatureAccess(userRole, FEATURES.SALES_PROFIT);
  const canViewPreviousBalance = hasFeatureAccess(userRole, FEATURES.PREVIOUS_BALANCE);
  const canViewSummary = hasFeatureAccess(userRole, FEATURES.SALES_SUMMARY);
  const isAdmin = userRole === 'ADMIN';

  // Use the custom hook for API integration
  const {
    sale,
    products,
    loading,
    error,
    actionLoading,
    updateProductQuantity,
    updateProductSellPrice,
    addProduct,
    deleteProduct,
    refreshData,
    setSale,
    setProducts,
  } = useViewSalesData(id);

  // Local state for editable fields - initialize from API data
  const [discount, setDiscount] = useState(sale?.flatDiscount || 0);
  const [shippingFee, setShippingFee] = useState(sale?.shippingAmount || 0);
  const [paidAmount, setPaidAmount] = useState(sale?.paidAmount || 0);

  // Debounced values for API calls - reduced to 1 second for better responsiveness
  const debouncedDiscount = useDebounce(discount, 1000);
  const debouncedShippingFee = useDebounce(shippingFee, 1000);
  const debouncedPaidAmount = useDebounce(paidAmount, 1000);

  // Debounced products for auto-save - reduced to 1 second for better responsiveness
  const debouncedProducts = useDebounce(products, 1000);

  // Track if any fields have been modified
  const [hasModifications, setHasModifications] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [autoSaveLoading, setAutoSaveLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastAutoSaveTime, setLastAutoSaveTime] = useState(0);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [lastInputTime, setLastInputTime] = useState(0);

  // Modal states
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [showDeleteProductModal, setShowDeleteProductModal] = useState(false);
  const [showLastProductErrorModal, setShowLastProductErrorModal] = useState(false);
  const [showProfits, setShowProfits] = useState(false);
  const [hoveredBuyPrice, setHoveredBuyPrice] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedCustomerName, setEditedCustomerName] = useState(
    sale?.customerName || ""
  );
  const [imageLoadingStates, setImageLoadingStates] = useState({});

  // Customer dropdown states
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(false);

  // Update local state when sale data changes
  useEffect(() => {
    if (sale) {
      setDiscount(sale.flatDiscount || 0);
      setShippingFee(sale.shippingAmount || 0);
      setPaidAmount(sale.paidAmount || 0);
      setEditedCustomerName(sale.customerName || "");
      // Reset modifications when sale data is loaded
      setHasModifications(false);
      // Mark as initialized after a shorter delay to enable auto-save
      setTimeout(() => {
        setIsInitialized(true);
      }, 1000);
    }
  }, [sale]);

  // Helper function to format number to 2 decimal places
  const formatToTwoDecimals = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return 0;
    return Math.round(num * 100) / 100; // Round to 2 decimal places
  };

  // Track modifications for discount
  const handleDiscountChange = (value) => {
    // Disable editing if status is not draft
    if (sale?.status?.toLowerCase() !== 'draft') {
      return;
    }
    setHasUserInteracted(true);
    setLastInputTime(Date.now());
    const numValue = parseFloat(value);
    // Prevent negative values
    if (numValue < 0) {
      setDiscount(0);
      return;
    }
    const formattedValue = formatToTwoDecimals(value);
    setDiscount(formattedValue);
    setHasModifications(true);
  };

  // Track modifications for shipping fee
  const handleShippingFeeChange = (value) => {
    // Disable editing if status is not draft
    if (sale?.status?.toLowerCase() !== 'draft') {
      return;
    }
    setHasUserInteracted(true);
    setLastInputTime(Date.now());
    const numValue = parseFloat(value);
    // Prevent negative values
    if (numValue < 0) {
      setShippingFee(0);
      return;
    }
    const formattedValue = formatToTwoDecimals(value);
    setShippingFee(formattedValue);
    setHasModifications(true);
  };

  // Track modifications for paid amount
  const handlePaidAmountChange = (value) => {
    // Disable editing if status is not draft
    if (sale?.status?.toLowerCase() !== 'draft') {
      return;
    }
    setHasUserInteracted(true);
    setLastInputTime(Date.now());
    const numValue = parseFloat(value);
    // Prevent negative values
    if (numValue < 0) {
      setPaidAmount(0);
      return;
    }
    const formattedValue = formatToTwoDecimals(value);
    setPaidAmount(formattedValue);
    setHasModifications(true);
  };

  // Auto-save function for debounced changes
  const autoSaveChanges = async () => {
    if (!sale || autoSaveLoading || !hasUserInteracted) return;

    try {
      setAutoSaveLoading(true);

      // Format products into items array according to API schema
      const items =
        debouncedProducts?.map((product) => {
          const itemData = {
            product: product.productId || product.id,
            qty: product.quantity,
            sell_price: product.sellPrice,
          };
          
          // Include unique_id only if it exists (was scanned by barcode)
          if (product.unique_id !== null && product.unique_id !== undefined && product.unique_id !== "") {
            itemData.unique_id = product.unique_id;
          }
          
          return itemData;
        }) || [];

      const cartData = {
        customer: selectedCustomer?.id || sale?.customer || null,
        shipping_amount: debouncedShippingFee.toString(),
        send_by_post: false,
        previous_balance: previousBalance.toString(),
        paid_amount: debouncedPaidAmount.toString(),
        flat_discount: debouncedDiscount.toString(),
        status: "DRAFT",
        items: items,
      };

      console.log('Auto-save cart data:', JSON.stringify(cartData, null, 2));
      const response = await salesAPI.updateCart(id, cartData);

      // Update local state with server response to show real-time updates
      if (response && response.data) {
        const serverData = response.data;
        
        // Update sale totals from server response
        setSale(prev => prev ? {
          ...prev,
          amount: parseFloat(serverData.total_amount) || 0,
          subTotal: parseFloat(serverData.sub_total) || 0,
          netProfit: parseFloat(serverData.net_profit) || 0,
          resultingBalance: parseFloat(serverData.resulting_balance) || 0,
          flatDiscount: parseFloat(serverData.flat_discount) || 0,
          shippingAmount: parseFloat(serverData.shipping_amount) || 0,
          paidAmount: parseFloat(serverData.paid_amount) || 0,
          previousBalance: parseFloat(serverData.previous_balance) || 0
        } : null);

        // Update products with server-calculated values
        if (serverData.cart_items) {
          setProducts(prev => prev.map(product => {
            const serverItem = serverData.cart_items.find(item => 
              item.id === product.id || item.product === product.productId
            );
            if (serverItem) {
              return {
                ...product,
                effectivePrice: parseFloat(serverItem.effective_price) || 0,
                subTotal: parseFloat(serverItem.sub_total) || 0,
                discountedSubTotal: parseFloat(serverItem.discounted_sub_total) || 0,
                sellPrice: parseFloat(serverItem.price) || 0,
                quantity: serverItem.qty || product.quantity,
                in_stock: serverItem.product_detail?.in_stock || product.in_stock || 0
              };
            }
            return product;
          }));
        }
      }
    } catch (error) {
      console.error("Error in auto-save:", error);
      
      // Extract and show backend validation errors
      let errorMessage = "Failed to save changes.";
      
      if (error.response?.data?.errors?.items) {
        // Handle items validation error (e.g., stock issues)
        errorMessage = error.response.data.errors.items;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Reload data to reset to last valid state
      await refreshData();
    } finally {
      setAutoSaveLoading(false);
    }
  };

  // Auto-save effect for debounced values
  useEffect(() => {
    if (!sale || autoSaveLoading || !isInitialized || !hasUserInteracted)
      return;

    // Prevent auto-save if it was called recently (within 3 seconds)
    const now = Date.now();
    if (now - lastAutoSaveTime < 3000) {
      return;
    }

    // Prevent auto-save if user is still actively typing (within 1 second of last input)
    const timeSinceLastInput = now - lastInputTime;
    if (timeSinceLastInput < 1000) {
      return;
    }

    // More precise comparison with tolerance for floating point numbers
    const hasDiscountChange =
      Math.abs(debouncedDiscount - (sale.flatDiscount || 0)) > 0.01;
    const hasShippingChange =
      Math.abs(debouncedShippingFee - (sale.shippingAmount || 0)) > 0.01;
    const hasPaidAmountChange =
      Math.abs(debouncedPaidAmount - (sale.paidAmount || 0)) > 0.01;

    // Only auto-save if there are actual meaningful changes
    if (hasDiscountChange || hasShippingChange || hasPaidAmountChange) {
      setLastAutoSaveTime(now);
      autoSaveChanges();
    }
  }, [
    debouncedDiscount,
    debouncedShippingFee,
    debouncedPaidAmount,
    autoSaveLoading,
    isInitialized,
    hasUserInteracted,
    lastAutoSaveTime,
    lastInputTime,
    sale?.flatDiscount,
    sale?.shippingAmount,
    sale?.paidAmount,
  ]);

  // Auto-save effect for product changes
  useEffect(() => {
    if (
      !sale ||
      autoSaveLoading ||
      !debouncedProducts ||
      !isInitialized ||
      !hasUserInteracted
    )
      return;

    // Prevent auto-save if it was called recently (within 3 seconds)
    const now = Date.now();
    if (now - lastAutoSaveTime < 3000) {
      return;
    }

    // Prevent auto-save if user is still actively typing (within 1 second of last input)
    const timeSinceLastInput = now - lastInputTime;
    if (timeSinceLastInput < 1000) {
      return;
    }

    // Check if products have actually changed
    const hasProductChanges = 
      debouncedProducts.length !== (sale.cart_items?.length || sale.items?.length || 0);

    if (hasProductChanges) {
      setLastAutoSaveTime(now);
      autoSaveChanges();
    }
  }, [
    debouncedProducts,
    autoSaveLoading,
    isInitialized,
    hasUserInteracted,
    lastAutoSaveTime,
    lastInputTime,
    sale?.cart_items,
    sale?.items,
  ]);

  // Calculate totals from API data or local calculations
  const totalQty =
    products?.reduce((sum, product) => sum + (product.quantity || 0), 0) || 0;
  const totalBuyPrice =
    products?.reduce(
      (sum, product) => sum + (product.buyPrice || 0) * (product.quantity || 0),
      0
    ) || 0;
  const totalSellPrice =
    products?.reduce(
      (sum, product) =>
        sum + (parseFloat(product.sellPrice) || 0) * (product.quantity || 0),
      0
    ) || 0;

  // Use API data if available, otherwise calculate locally
  const totalAmount = sale?.amount || totalSellPrice + shippingFee - discount;
  const previousBalance = sale?.previousBalance || 0;
  const subTotal = sale?.subTotal || totalAmount + previousBalance;
  const resultingBalance = sale?.resultingBalance || subTotal - paidAmount;
  const netProfit = sale?.netProfit || totalSellPrice - totalBuyPrice;

  const updateQuantity = (productId, newQuantity) => {
    // Disable editing if status is not draft
    if (sale?.status?.toLowerCase() !== 'draft') {
      return;
    }
    setHasUserInteracted(true);
    setLastInputTime(Date.now());
    // Prevent negative quantities
    if (newQuantity < 0) {
      return;
    }
    
    // Check stock availability only when INCREASING quantity
    const product = products.find(p => p.id === productId);
    if (product && product.in_stock !== undefined && product.in_stock !== null) {
      // Only validate stock when user is trying to increase quantity
      // in_stock represents remaining stock that can be added
      if (newQuantity > product.quantity && product.in_stock <= 0) {
        toast.error(`No more items available in stock.`, {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        return;
      }
    }
    
    updateProductQuantity(productId, newQuantity);
    setHasModifications(true);
  };

  const updateSellPrice = (productId, newPrice) => {
    // Disable editing if status is not draft
    if (sale?.status?.toLowerCase() !== 'draft') {
      return;
    }
    setHasUserInteracted(true);
    setLastInputTime(Date.now());
    
    // Handle empty string - allow it to be empty
    if (newPrice === '' || newPrice === null || newPrice === undefined) {
      updateProductSellPrice(productId, '');
      setHasModifications(true);
      return;
    }
    
    const numPrice = parseFloat(newPrice);
    // Prevent negative prices
    if (numPrice < 0) {
      return;
    }
    
    // If it's a valid number, format it
    if (!isNaN(numPrice)) {
      const formattedPrice = formatToTwoDecimals(newPrice);
      updateProductSellPrice(productId, formattedPrice);
    } else {
      // If it's not a valid number, just set the raw value
      updateProductSellPrice(productId, newPrice);
    }
    setHasModifications(true);
  };

  const handleDeleteProduct = (productId) => {
    // Disable editing if status is not draft
    if (sale?.status?.toLowerCase() !== 'draft') {
      return;
    }
    
    // Check if this is the last product
    if (products.length <= 1) {
      setShowDeleteProductModal(false);
      setDeletingProduct(null);
      setShowLastProductErrorModal(true);
      return;
    }
    
    deleteProduct(productId);
    setShowDeleteProductModal(false);
    setDeletingProduct(null);
    setHasModifications(true);
  };

  const handleEditName = () => {
    // Disable editing if status is not draft
    if (sale?.status?.toLowerCase() !== 'draft') {
      return;
    }
    setIsEditingName(true);
  };

  const handleSaveName = async () => {
    if (!selectedCustomer) return;
    
    setEditedCustomerName(selectedCustomer.name);
    setIsEditingName(false);
    setHasUserInteracted(true);
    setLastInputTime(Date.now());
    
    // Trigger auto-save with new customer
    setTimeout(() => {
      autoSaveChanges();
    }, 100);
  };

  const handleCancelEdit = () => {
    setEditedCustomerName(sale?.customerName || "");
    setSelectedCustomer(null);
    setIsEditingName(false);
  };

  // Fetch customers when dropdown opens
  const fetchCustomers = async () => {
    setCustomerLoading(true);
    try {
      const { response, error } = await customersAPI.getCustomers(
        customerSearchTerm,
        1,
        50
      );
      if (error) {
        console.error("Error fetching customers:", error);
        setCustomers([]);
      } else {
        let customersList = [];
        if (response) {
          if (Array.isArray(response)) {
            customersList = response;
          } else if (response.results && Array.isArray(response.results)) {
            customersList = response.results;
          } else if (response.data && Array.isArray(response.data)) {
            customersList = response.data;
          }
        }
        setCustomers(customersList);
      }
    } catch (err) {
      console.error("Error fetching customers:", err);
      setCustomers([]);
    } finally {
      setCustomerLoading(false);
    }
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setEditedCustomerName(customer.name);
    setIsCustomerDropdownOpen(false);
    setCustomerSearchTerm("");
    setHasModifications(true);
  };

  // Wrapper function to track product additions
  const handleAddProduct = async (product) => {
    // Disable editing if status is not draft
    if (sale?.status?.toLowerCase() !== 'draft') {
      return;
    }
    
    // Check if product already exists
    const existingProduct = products.find(p => {
      // For products with unique_id (barcode scanned), match by unique_id only
      if (product.unique_id) {
        return p.unique_id === product.unique_id;
      }
      // For regular products, match by product ID only if both don't have unique_id
      if (!p.unique_id) {
        return p.productId === product.id || p.id === product.id;
      }
      return false;
    });
    
    if (existingProduct) {
      // Increase quantity of existing product
      updateQuantity(existingProduct.id, existingProduct.quantity + (product.quantity || 1));
      toast.info(`Increased quantity of "${existingProduct.name}"`, {
        position: "top-right",
        autoClose: 2000,
      });
    } else {
      // Add new product
      addProduct(product);
    }
    
    setHasModifications(true);
    setHasUserInteracted(true);
    setLastInputTime(Date.now());
    
    // Trigger auto-save immediately after adding product
    setTimeout(() => {
      autoSaveChanges();
    }, 100);
  };

  // Handle save changes
  const handleSaveChanges = async () => {
    if (!hasModifications) {
      toast.info("No changes to save.", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    setSaveLoading(true);
    try {
      // Format products into items array according to API schema
      const items =
        products?.map((product) => {
          const itemData = {
            product: product.productId || product.id, // Use productId if available, fallback to id
            qty: product.quantity,
            sell_price: product.sellPrice,
            discounted_sub_total: product.discounted_sub_total || 0,
          };
          
          // Include unique_id only if it exists (was scanned by barcode)
          if (product.unique_id !== null && product.unique_id !== undefined && product.unique_id !== "") {
            itemData.unique_id = product.unique_id;
          }
          
          return itemData;
        }) || [];


      const cartData = {
        customer: selectedCustomer?.id || sale?.customer || null,
        shipping_amount: shippingFee.toString(),
        send_by_post: false,
        previous_balance: previousBalance.toString(),
        paid_amount: paidAmount.toString(),
        flat_discount: discount.toString(),
        status: "DRAFT",
        items: items,
      };

      await salesAPI.updateCart(id, cartData);
      
      setHasModifications(false);
      toast.success("Changes saved successfully!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      await refreshData();
    } catch (error) {
      console.error("Error saving changes:", error);
      
      // Extract and show backend validation errors
      let errorMessage = "Failed to save changes. Please try again.";
      
      if (error.response?.data?.errors?.items) {
        // Handle items validation error (e.g., stock issues)
        errorMessage = error.response.data.errors.items;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Reload data to reset to last valid state
      await refreshData();
    } finally {
      setSaveLoading(false);
    }
  };

  // Handle Publish button click
  const handlePublishClick = () => {
    if (actionLoading || sale?.status !== "draft" || hasModifications) {
      if (hasModifications) {
        toast.warning("Please save your changes before publishing the sale.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } else if (sale?.status !== "draft") {
        toast.info("This sale cannot be published in its current status.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
      return;
    }
    setShowPublishModal(true);
  };

  // Handle actual publish
  const handlePublishConfirm = async () => {
    try {
      await salesAPI.publishSale(id);
      toast.success("Sale published successfully!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setShowPublishModal(false);
      // Refresh data to get updated status
      refreshData();
    } catch (error) {
      console.error("Error publishing sale:", error);
      
      // Extract error message from backend response
      let errorMessage = "Failed to publish sale. Please try again.";
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  // Handle cancel sale
  const handleCancelConfirm = async (reason) => {
    try {
      await salesAPI.cancelSale(id);
      toast.success("Sale cancelled successfully!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setShowCancelModal(false);
      // Refresh data to get updated status
      refreshData();
    } catch (error) {
      console.error("Error cancelling sale:", error);
      toast.error("Failed to cancel sale. Please try again.", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const filteredCustomers = customers.filter((customer) =>
    customer.name.toLowerCase().includes(customerSearchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isCustomerDropdownOpen &&
        !event.target.closest(".customer-dropdown-container")
      ) {
        setIsCustomerDropdownOpen(false);
      }
    };

    if (isCustomerDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCustomerDropdownOpen]);

  // Fetch customers when dropdown opens or search term changes
  useEffect(() => {
    if (isCustomerDropdownOpen) {
      fetchCustomers();
    }
  }, [isCustomerDropdownOpen, customerSearchTerm]);

  const saleData = {
    totalAmount,
    paidAmount,
    resultingBalance,
  };

  // Show loading state
  if (loading) {
    return (
      <div className="figma-sales-view">
        <div className="loading-container">
          <p>Loading sale details...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error || !sale) {
    return (
      <div className="figma-sales-view">
        <div className="error-container">
          <p>Error loading sale details: {error || "Sale not found"}</p>
          <button onClick={refreshData} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="figma-sales-view">
      {/* Back Button - Outside main container */}
      <div className="figma-back-container">
        <button className="figma-back-btn" onClick={() => navigate("/sales")}>
          <ArrowLeft size={15} />
          Back to Sales
        </button>
      </div>

      {/* Customer Info Card - Separate Card */}
      <div className="figma-customer-card">
        <div className="figma-customer-header">
          <div className="figma-customer-info">
            <div className="d-flex align-items-center gap-2">
              {isEditingName ? (
                <div className="d-flex align-items-center gap-2">
                  <div className="customer-dropdown-container">
                    <div
                      className="dropdown-input"
                      onClick={() =>
                        setIsCustomerDropdownOpen(!isCustomerDropdownOpen)
                      }
                    >
                      <span className="selected-value">
                        {selectedCustomer
                          ? selectedCustomer.name
                          : editedCustomerName || "Select customer"}
                      </span>
                      <ChevronDown className="dropdown-arrow" size={16} />
                    </div>

                    {isCustomerDropdownOpen && (
                      <div className="dropdown-menu">
                        <div className="dropdown-search">
                          <Search className="batches-search-icon" size={16} />
                          <input
                            type="text"
                            className="batches-search-input"
                            placeholder="Search customer name..."
                            value={customerSearchTerm}
                            onChange={(e) =>
                              setCustomerSearchTerm(e.target.value)
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="dropdown-list noscrollbar">
                          {customerLoading ? (
                            <div className="batches-dropdown-item">
                              Loading customers...
                            </div>
                          ) : filteredCustomers.length > 0 ? (
                            filteredCustomers.map((customer) => (
                              <div
                                key={customer.id}
                                className="batches-dropdown-item"
                                onClick={() => handleCustomerSelect(customer)}
                              >
                                {customer.name}
                              </div>
                            ))
                          ) : (
                            <div className="batches-dropdown-item no-results">
                              No customers found
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <button
                    className="figma-name-save-btn"
                    onClick={handleSaveName}
                    title="Save"
                  >
                    <Check size={16} />
                  </button>
                  <button
                    className="figma-name-cancel-btn"
                    onClick={handleCancelEdit}
                    title="Cancel"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="figma-customer-name">
                    {editedCustomerName || sale?.customerName}
                  </h2>
                  <button
                    className="figma-name-edit-btn"
                    onClick={handleEditName}
                    title="Edit customer name"
                    disabled={sale?.status?.toLowerCase() !== 'draft'}
                  >
                    <NameEditIcon />
                  </button>
                </>
              )}
            </div>
            <p className="figma-customer-date">
              {sale?.createdAt
                ? new Date(sale.createdAt).toLocaleString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "N/A"}
            </p>
          </div>

          <StatusBadge status={sale?.status} />
        </div>

        <div className="figma-customer-details">
          <div className="figma-detail-item">
            <span className="figma-label">Creator</span>
            <span className="figma-value">{sale?.creator_name || "N/A"}</span>
          </div>
          <div className="figma-detail-item">
            <span className="figma-label">Invoice ID</span>
            <span className="figma-value">{sale?.invoiceId}</span>
          </div>
          {canViewPreviousBalance && (
            <div className="figma-detail-item">
              <span className="figma-label">Previous Balance</span>
              <span className="">
                <span
                  className={`balance-badge ${
                    previousBalance >= 0
                      ? "positive"
                      : "negative"
                  }`}
                >
                  {previousBalance >= 0 ? `€${(previousBalance).toFixed(2)}` : `€${previousBalance.toFixed(2)}`}
                </span>
              </span>
            </div>
          )}
          <div className="figma-detail-item">
            <span className="figma-label">Total Qty</span>
            <span className="figma-value">{totalQty}</span>
          </div>
          <div className="figma-detail-item">
            <span className="figma-label">Total Amount</span>
            <span className="figma-value">€{totalAmount.toFixed(2)}</span>
          </div>
          {canViewProfits && (
            <div className="figma-detail-item">
              <span className="figma-label">Net Profit</span>
              <div className="d-flex align-items-center gap-2">
                <span
                  className={`sales-detail-profit-stars ${
                    netProfit < 0 ? "negative" : ""
                  }`}
                >
                  {showProfits ? `€${netProfit.toFixed(2)}` : "*******"}
                </span>
                <button
                  className="profit-toggle-btn"
                  onClick={() => setShowProfits(!showProfits)}
                  title={showProfits ? "Hide profit" : "Show profit"}
                >
                  {showProfits ? <EyeClosed size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Two Card Layout */}
      <div className={`figma-cards-container ${canViewSummary ? 'two-columns' : 'single-column'}`}>
        {/* Products Section - Left Card (Wider) */}
        <div className="figma-products-card">
          <div className="figma-products-header">
            <h3>Products</h3>
            <button
              className="figma-add-product-btn"
              onClick={() => setShowAddProductModal(true)}
              disabled={sale?.status?.toLowerCase() !== 'draft'}
            >
              <Plus size={16} />
              Add Product
            </button>
          </div>

          {/* Products List */}
          <div className="figma-products-list">
            {products && products.length > 0 ? (
              products.map((product) => (
                <div key={product.id} className="figma-product-item">
                  {/* Product Image */}
                  <div className="figma-product-image-container">
                    {product.image &&
                    product.image !== "/placeholder-product.png" ? (
                      <>
                        {imageLoadingStates[product.id] && (
                          <div className="image-loading">Loading...</div>
                        )}
                        <img
                          src={product.image}
                          alt={product.name}
                          className="figma-product-image"
                          onLoad={() => {
                            
                            setImageLoadingStates((prev) => ({
                              ...prev,
                              [product.id]: false,
                            }));
                          }}
                          onError={(e) => {
                            setImageLoadingStates((prev) => ({
                              ...prev,
                              [product.id]: false,
                            }));
                            // Hide the image on error and show placeholder
                            e.target.style.display = "none";
                            // Show placeholder instead
                            const placeholder = e.target.nextElementSibling;
                            if (placeholder) {
                              placeholder.style.display = "flex";
                            }
                          }}
                          onLoadStart={() => {
                            setImageLoadingStates((prev) => ({
                              ...prev,
                              [product.id]: true,
                            }));
                          }}
                          style={{
                            display: imageLoadingStates[product.id]
                              ? "none"
                              : "block",
                          }}
                        />
                        <div
                          className="no-image-placeholder"
                          style={{ display: "none" }}
                        >
                          <span>No Image</span>
                        </div>
                      </>
                    ) : (
                      <div className="no-image-placeholder">
                        <span>No Image</span>
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="figma-product-info">
                    <div className="figma-product-name-container">
                      <h4 className="figma-product-name">{product.name}</h4>

                      {/* Product details in same format as ProductTable */}
                      <div
                        style={{
                          fontSize: "0.8em",
                          color: "#666",
                          marginTop: "4px",
                        }}
                      >
                        {product.ram && `${product.ram}`}
                        {product.ram && product.storage && " | "}
                        {product.storage && `${product.storage}`}
                        {product.variants && product.variants.length > 0 && (
                          <>
                            {(product.ram || product.storage) && " | "}
                            {product.variants
                              .map((variant) =>
                                Object.values(variant).join(", ")
                              )
                              .join(", ")}
                          </>
                        )}
                        {product.suffix && (
                          <>
                            {(product.ram ||
                              product.storage ||
                              (product.variants &&
                                product.variants.length > 0)) &&
                              " | "}
                            {product.suffix}
                          </>
                        )}
                        {product.used_mobile !== undefined &&
                          product.used_mobile !== null && (
                            <>
                              {(product.ram ||
                                product.storage ||
                                (product.variants &&
                                  product.variants.length > 0) ||
                                product.suffix) &&
                                " | "}
                              {product.used_mobile === false ? "New" : "Used"}
                            </>
                          )}
                      </div>

                      {/* {product.category && (
                        <p className="figma-product-category">
                          {product.category}
                        </p>
                      )} */}
                    </div>

                    {/* Quantity Control */}
                    <div className="figma-quantity-control">
                      <button
                        className="figma-qty-btn"
                        onClick={() =>
                          updateQuantity(product.id, product.quantity - 1)
                        }
                        disabled={sale?.status?.toLowerCase() !== 'draft'}
                      >
                        <Minus size={16} />
                      </button>
                      <span className="figma-quantity">{product.quantity}</span>
                      <button
                        className="figma-qty-btn"
                        onClick={() =>
                          updateQuantity(product.id, product.quantity + 1)
                        }
                        disabled={
                          sale?.status?.toLowerCase() !== 'draft' || 
                          product.in_stock === 0
                        }
                        style={{
                          cursor: (sale?.status?.toLowerCase() !== 'draft' || product.in_stock === 0) 
                            ? 'not-allowed' 
                            : 'pointer'
                        }}
                        title={
                          product.in_stock === 0
                            ? `No more items available in stock`
                            : product.in_stock > 0
                            ? `${product.in_stock} more items available`
                            : ''
                        }
                      >
                        <Plus size={16} />
                      </button>
                    </div>

                    {/* Product Pricing */}
                    <div className="figma-product-pricing">
                      {canViewProfits && (
                        <div className="figma-buy-price-item">
                          <span className="figma-price-label">Buy Price :</span>
                          <span
                            className="figma-price-value buy-price-hover"
                            onMouseEnter={() => setHoveredBuyPrice(product.id)}
                            onMouseLeave={() => setHoveredBuyPrice(null)}
                          >
                            {hoveredBuyPrice === product.id
                              ? `€${(product.buyPrice || 0).toFixed(2)}`
                              : "******"}
                          </span>
                        </div>
                      )}

                      <div className="figma-sell-price-item">
                        <span className="figma-price-label">Sell Price :</span>

                        <input
                          type="number"
                          className="figma-sell-price-input"
                          value={product.sellPrice || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            updateSellPrice(product.id, value);
                          }}
                          onKeyDown={(e) => {
                            // Prevent negative sign and other invalid characters
                            if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                              e.preventDefault();
                            }
                          }}
                          step="0.01"
                          min="0"
                          style={{ width: "90px" }}
                          placeholder="0.00"
                          disabled={sale?.status?.toLowerCase() !== 'draft'}
                        />
                      </div>

                      <div className="figma-sub-total-item">
                        <span className="figma-price-label">Sub-Total:</span>
                        <span className="figma-price-value">
                          €{(product.discounted_sub_total || 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <button
                    className="action-btn delete"
                    title="Delete Sale"
                    onClick={() => {
                      setDeletingProduct(product);
                      setShowDeleteProductModal(true);
                    }}
                    disabled={sale?.status?.toLowerCase() !== 'draft'}
                  >
                    <RedDeleteIcon />
                  </button>
                </div>
              ))
            ) : (
              <div className="no-products">
                <p>No products found in this sale.</p>
              </div>
            )}
          </div>
        </div>

        {/* Summary Section - Right Card (Narrower) */}
        {canViewSummary && (
          <div className="figma-summary-card">
          <div className="figma-summary-header">
            <h3>Summary</h3>
          </div>

          <div className="figma-summary-content">
            <div className="figma-summary-row">
              <span className="figma-summary-label">Discount per Qty</span>
              <input
                type="number"
                className="figma-summary-input"
                placeholder="Enter Discount"
                value={discount || ""}
                onChange={(e) => handleDiscountChange(e.target.value)}
                step="0.01"
                min="0"
                disabled={sale?.status?.toLowerCase() !== 'draft'}
              />
            </div>
            <div className="figma-summary-row">
              <span className="figma-summary-label">Shipping Details</span>
              <input
                type="number"
                className="figma-summary-input"
                placeholder="Enter Shipping Fee"
                value={shippingFee || ""}
                onChange={(e) => handleShippingFeeChange(e.target.value)}
                step="0.01"
                min="0"
                disabled={sale?.status?.toLowerCase() !== 'draft'}
              />
            </div>

            <div className="figma-summary-row">
              <span className="figma-summary-label">Total Amount</span>
              <span className="figma-summary-value">
                €{totalAmount.toFixed(2)}
              </span>
            </div>
            {canViewPreviousBalance && (
              <div className="figma-summary-row">
                <span className="figma-summary-label">Previous Balance</span>
                <span className="figma-summary-value">
                  €{previousBalance.toFixed(2)}
                </span>
              </div>
            )}
            <div className="figma-summary-row">
              <span className="figma-summary-label">Sub-Total</span>
              <span className="figma-summary-value">
                €{subTotal.toFixed(2)}
              </span>
            </div>
            {isAdmin && (
              <div className="figma-summary-row">
                <span className="figma-summary-label">Paid Amount</span>
                <input
                  type="number"
                  className="figma-summary-input"
                  placeholder="Enter Amount"
                  value={paidAmount || ""}
                  onChange={(e) => handlePaidAmountChange(e.target.value)}
                  step="0.01"
                  min="0"
                  disabled={sale?.status?.toLowerCase() !== 'draft'}
                />
              </div>
            )}
            <div className="figma-summary-row figma-balance-row">
              <span className="resulting-balance-text">Resulting Balance</span>
              <span 
                className={`resulting-balance-text ${
                  resultingBalance < 0
                    ? "positive"
                    : "negative"
                }`}
              >
                {resultingBalance < 0 ? `€${(resultingBalance * -1).toFixed(2)}` : `-€${resultingBalance.toFixed(2)}`}
              </span>
            </div>

            {canViewProfits && (
              <div className="figma-net-profit">
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <span>
                    Net Profit:{" "}
                    {showProfits ? `€${netProfit.toFixed(2)}` : "*******"}
                  </span>
                </div>
              </div>
            )}
          </div>
          </div>
        )}
      </div>

      {/* Action Buttons - Right Side */}
      <div className="figma-actions-container">
        <button
          className="figma-btn figma-print-btn"
          onClick={() => navigate(`/sales/print/${id}`)}
        >
          <Printer size={16} />
          Print
        </button>
        {isAdmin && (
          <button
            className="figma-btn figma-publish-btn"
            onClick={handlePublishClick}
            disabled={
              actionLoading || sale?.status !== "draft" || hasModifications
            }
            title={hasModifications ? "Save changes before publishing" : ""}
          >
            <Check size={16} />
            {actionLoading
              ? "Publishing..."
              : hasModifications
              ? "Publish"
              : "Publish"}
          </button>
        )}
        <button
          className="figma-btn figma-cancel-btn"
          onClick={() => setShowCancelModal(true)}
          disabled={actionLoading || sale?.status === "cancelled" || sale?.status?.toLowerCase() !== 'draft'}
        >
          <X size={16} />
          {actionLoading ? "Cancelling..." : "Cancel Sale"}
        </button>
        <button
          className={`figma-btn figma-draft-save-btn ${
            hasModifications ? "has-changes" : ""
          }`}
          title={
            hasModifications ? "You have unsaved changes" : "Save current draft"
          }
          onClick={handleSaveChanges}
          disabled={saveLoading || autoSaveLoading || sale?.status?.toLowerCase() !== 'draft'}
        >
          <SaveIcon />
          {saveLoading
            ? "Saving..."
            : autoSaveLoading
            ? "Auto-saving..."
            : hasModifications
            ? "Save Changes"
            : "Save Draft"}
        </button>
      </div>

      {/* Modals */}
      <PublishSaleModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onConfirm={handlePublishConfirm}
        saleData={saleData}
        loading={actionLoading}
      />

      {/* Delete Modal */}
      {showDeleteProductModal && deletingProduct && (
        <>
          <div className="modal-overlay">
            <div className="delete-modal">
              <div className="modal-icon">
                <img src={DeleteIcon} alt="Delete" />
              </div>
              <h3 className="modal-title">Delete Product</h3>
              <p className="modal-message">
                Are you sure you want to delete product{" "}
                <b>{deletingProduct.name}</b>?
                <br />
                This action cannot be undone.
              </p>
              <div className="modal-actions">
                <button
                  className="modal-cancel-btn"
                  onClick={() => setShowDeleteProductModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="modal-delete-btn"
                  onClick={() => handleDeleteProduct(deletingProduct.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Last Product Error Modal */}
      {showLastProductErrorModal && (
        <>
          <div className="modal-overlay">
            <div className="delete-modal">
              <div className="modal-icon">
                <img src={DeleteIcon} alt="Error" />
              </div>
              <h3 className="modal-title">Cannot Delete Product</h3>
              <p className="modal-message">
                You cannot delete the last product. At least one product must remain in the sale.
              </p>
              <div className="modal-actions">
                <button
                  className="modal-cancel-btn"
                  onClick={() => setShowLastProductErrorModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <CancelSaleModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelConfirm}
        loading={actionLoading}
      />

      <AddProductModal
        isOpen={showAddProductModal}
        onClose={() => setShowAddProductModal(false)}
        onAddProduct={handleAddProduct}
      />
    </div>
  );
};

export default ViewSales;
